import { UploadFile } from 'antd/es/upload/interface';
import { ErrorMessage } from './axios';
import { appendFileIfExists } from './file';
import { resizeImage } from './validateUploadSize';
import { notification } from 'antd';
import { IRole } from 'app/modules/TeamMember/types';

type TranslateFunction = (key: string, variables?: Record<string, unknown>) => string;

interface DocumentUploadParams {
  file: File;
  key: string;
  currentUser: {
    agentId?: number | string;
    roles: IRole[] | string[];
  };
  fileList: UploadFile[];
  parseDocumentMutation: {
    mutate: (
      formData: FormData,
      options: {
        onSuccess: (response: any) => void;
        onError: (error: ErrorMessage) => void;
      },
    ) => void;
  };
  onParseSuccess: (data: any) => void;
  handleDocumentUpload: (params: { file: UploadFile; fileList: UploadFile[] }) => void;
  agencyId: string | number;
  tripId?: number;
  customerId?: number;
}

export const handleDocumentUploadWithOCR = ({
  file,
  key,
  currentUser,
  fileList,
  parseDocumentMutation,
  onParseSuccess,
  handleDocumentUpload,
  agencyId,
  tripId,
  customerId,
}: DocumentUploadParams) => {
  const formData = new FormData();
  appendFileIfExists(formData, 'file', [{ originFileObj: file }]);
  formData.append('ocrEnabled', 'true');
  formData.append(
    'documentInfo',
    JSON.stringify({
      documentUploaderId: currentUser?.agentId,
      documentUploaderRoles: currentUser?.roles?.map((role: IRole | string) =>
        typeof role === 'string' ? role : role.name,
      ),
      documentUploaderRole: 'ADMIN',
      documentType: key,
      agencyId: agencyId,
      tripId: tripId,
      customerId: customerId,
    }),
  );

  parseDocumentMutation.mutate(formData, {
    onSuccess: (response) => {
      onParseSuccess(response.data);
    },
    onError: () => {},
  });

  const newFile = {
    ...file,
    type: key,
    uid: `${Date.now()}`,
    name: file.name,
    status: 'done',
  } as unknown as UploadFile;

  const newFileList = [...fileList, newFile];
  handleDocumentUpload({
    file: newFile,
    fileList: newFileList,
  });
};

interface ProcessAndSetImageParams {
  file: File;
  t: TranslateFunction;
  setImage: (imageUrl: string | null, file?: File) => void;
  targetWidth?: number;
  targetHeight?: number;
  quality?: number;
  outputFormat?: string;
}

export const processAndSetImage = async ({
  file,
  t,
  setImage,
  targetWidth = 1120,
  targetHeight = 150,
  quality = 0.9,
  outputFormat = 'image/jpeg',
}: ProcessAndSetImageParams) => {
  try {
    // Resize the image to specified dimensions
    const resizedFile = await resizeImage(file, t, targetWidth, targetHeight, quality, outputFormat);

    // Create URL for the resized image
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImage(e.target.result as string, resizedFile as File);
      }
    };
    reader.readAsDataURL(resizedFile as Blob);
  } catch (error) {
    notification.error({
      message: t('upload.error.imageLoadFailed'),
      duration: 5,
    });
  }
};
